<template>
	<AppLayout
		:title="trans('menu.association.representatives.admissions')"
		:subtitle="`${dayjs(election.starts_at).format('DD MMM YY HH:mm')} - ${dayjs(election.ends_at).format('DD MMM YY HH:mm')}`"
		:return-url="route('association.representatives.admissions.index')"
		:return-label="trans('menu.association.representatives.admissions')"
	>
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="vote">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('rotations.admissions.vote.title') }}
			</PrimaryButton>
		</template>
		<div class="grow p-4 flex flex-col gap-3 overflow-y-auto">
			<p>A continuación, la Comisión Directiva resolverá sobre la admisión como Asociados Activos de los Representantes electos en los últimos Recambios de Representantes de las Universidades Activas.</p>
			<p class="text-lg font-bold">Personas habilitadas para votar</p>
			<p>Solo votarán los miembros de Comisión Directiva Titulares (un voto cada uno) y en ningún caso puede haber más de 10 votantes.</p>
			<p class="text-lg font-bold">Validez de la votación</p>
			<p>Para darse por válido el resultado de la votación, se necesita un mínimo de 7 votos positivos, correspondientes a las dos terceras partes de los 10 titulares de la Comisión Directiva requeridas en el Estatuto.</p>
			<p class="text-lg font-bold">Resolución</p>
			<p>La resolución se hará por mayoría simple (la opción con más votos). La no emisión del voto en tiempo y forma se considerará como abstención.</p>
			<p>En caso de empate, la decisión final recae en el Presidente de AArEII o quien lo esté supliendo en caso de ausencia y según el Estatuto.</p>
			<p class="text-lg font-bold">Contexto</p>
			<p>El Artículo Décimo del Estatuto dice que cada Representantes entrante deberá:</p>
			<Quote>
				<ol class="list-[lower-alpha] list-inside" start="7">
					<li>solicitar por escrito la admisión a la Comisión Directiva, acreditando bajo declaración firmada, que reúne las condiciones exigidas para ser asociado activo;</li>
					<li>ser aceptado por la Comisión Directiva, quién examinará los antecedentes del solicitante, pudiendo recabar información complementaria si lo estimare pertinente y resolverá la admisión o rechazo del postulante, en votación secreta. Queda prohibido a los miembros de la Comisión Directiva bajo pena de separación de su cargo, dar explicaciones o noticias respecto de la admisión o rechazo de postulantes;</li>
				</ol>
			</Quote>
			<template v-for="rotation in rotations" :key="`rotation-${rotation.id}`">
				<LabeledObject>
					<template #label>{{ trans('rotations.admissions.rotation_date') }}: {{ dayjs(rotation.begins_at).format(trans('general.dayjs.date')) }}</template>
					<p class="text-2xl font-bold">{{ rotation.college.name }} ({{ rotation.college.acronym }})</p>
				</LabeledObject>
				<Card v-for="(candidate, candidateIndex) in rotation.elected_candidates" :key="`candidate-${candidate.id}`">
					<LabeledObject full>
						<template #label>{{ trans('rotations.admissions.candidates.title', {number: `${candidateIndex + 1}`}) }}</template>
						<p class="text-lg font-bold">{{ candidate.user.full_name }}</p>
					</LabeledObject>
					<CandidateProfile :candidate="candidate"/>
					<div class="px-5 py-2 rounded-full bg-gray-200 dark:bg-slate-900 flex items-center gap-5 w-fit">
						<p class="text-lg font-bold">{{ trans('rotations.admissions.vote.title') }}</p>
						<div class="flex items-center gap-3 cursor-pointer text-green-800 dark:text-green-200" @click.stop="voteCandidateToAdmit(candidate)">
							<i v-if="votedCandidateToAdmit(candidate)" class="fa-solid fa-circle-check"/>
							<i v-else class="fa-regular fa-circle"/>
							<p>{{ trans('rotations.admissions.vote.admit') }}</p>
						</div>
						<div class="flex items-center gap-3 cursor-pointer text-red-800 dark:text-red-200" @click.stop="voteCandidateToReject(candidate)">
							<i v-if="votedCandidateToReject(candidate)" class="fa-solid fa-circle-xmark"/>
							<i v-else class="fa-regular fa-circle"/>
							<p>{{ trans('rotations.admissions.vote.reject') }}</p>
						</div>
						<div class="flex items-center gap-3 cursor-pointer" @click.stop="voteCandidateToAbstain(candidate)">
							<i v-if="votedCandidateToAbstain(candidate)" class="fa-solid fa-circle-dot"/>
							<i v-else class="fa-regular fa-circle"/>
							<p>{{ trans('rotations.admissions.vote.abstain') }}</p>
						</div>
					</div>
				</Card>
			</template>
		</div>
	</AppLayout>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import axios from 'axios'
import {route} from 'ziggy-js'
import {ref, type Ref} from 'vue'
import {trans} from 'laravel-vue-i18n'
import {router} from '@inertiajs/vue3'
import {Color} from '@/Types/Enums/Color'
import Quote from '@/Components/UI/Quote.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Card from '@/Components/UI/Cards/Card.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type Rotation} from '@/Types/Periods/Rotations/Rotation'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type AdmissionElection} from '@/Types/Elections/AdmissionElection'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import CandidateProfile from '@/Components/Audit/Rotations/CandidateProfile.vue'
import {type RotationCandidate} from '@/Types/Periods/Rotations/RotationCandidate'

interface Vote {
	vote_id:number,
	value:number|null,
}

const props = defineProps<{
	votes:Array<Vote>,
	rotations:Array<Rotation>,
	election:AdmissionElection,
}>()

const working:Ref<boolean> = ref(false)
const votes:Ref<Array<Vote>> = ref(props.votes)
const rotations:Ref<Array<Rotation>> = ref(props.rotations)
const election:Ref<AdmissionElection> = ref(props.election)

function findCandidateIndexById(candidateId:number):number{
	return votes.value.findIndex((vote:Vote) => vote.vote_id === candidateId)
}

function voteCandidateToAdmit(candidate:RotationCandidate):void{
	const index = findCandidateIndexById(candidate.id)
	if(index > -1){
		votes.value[index].value = 1
	}
}

function voteCandidateToReject(candidate:RotationCandidate):void{
	const index = findCandidateIndexById(candidate.id)
	if(index > -1){
		votes.value[index].value = 0
	}
}

function voteCandidateToAbstain(candidate:RotationCandidate):void{
	const index = findCandidateIndexById(candidate.id)
	if(index > -1){
		votes.value[index].value = null
	}
}

function votedCandidateToAdmit(candidate:RotationCandidate):boolean{
	const index = findCandidateIndexById(candidate.id)
	if(index === -1){
		return false
	}
	return votes.value[index].value === 1
}

function votedCandidateToReject(candidate:RotationCandidate):boolean{
	const index = findCandidateIndexById(candidate.id)
	if(index === -1){
		return false
	}
	return votes.value[index].value === 0
}

function votedCandidateToAbstain(candidate:RotationCandidate):boolean{
	const index = findCandidateIndexById(candidate.id)
	if(index === -1){
		return false
	}
	return votes.value[index].value === null
}

function vote():void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.post(route('association.representatives.admissions.vote.store', election.value.id), {
			votes: votes.value,
		}).then(() => {
			showSuccess(trans('general.updated'))
			router.get(route('home'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>